import {
  band,
  breakpoints,
  Col,
  frosted,
  IconButton,
  type IconName,
  motion,
  onWeb,
  ScrollArea,
  Text,
  useDragSize,
  useTheme,
  type ViewStyle,
} from "@rally/ui-kit";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { say } from "../../localization/say.ts";
import { CONTENT_HEADER_HEIGHT } from "../reading.ts";
import { Drawer as TheDrawer } from "./drawer-pane.tsx";
import { FocusBanner } from "./focus-banner.tsx";
import { FocusChromeProvider, type FocusRegistration } from "./panel-focus.tsx";
import { showDetails } from "./showing.ts";
import type { Card } from "./stacking.ts";

export const A_PANEL_IS = band.trail;
const A_PANEL_LEAST = 280;
const A_PANEL_MOST = 560;
const TRAIL_KEY = "rally.panel.width";

function rememberedTrail(): number {
  try {
    const said = typeof localStorage === "undefined" ? null : Number(localStorage.getItem(TRAIL_KEY));
    return said && said >= A_PANEL_LEAST && said <= A_PANEL_MOST ? said : A_PANEL_IS;
  } catch {
    return A_PANEL_IS;
  }
}

export const A_RESOURCE_PANEL_IS = A_PANEL_IS * 2.25;

const resourcePanelWidth = (panel: number) =>
  `min(${A_RESOURCE_PANEL_IS}px, max(0px, calc(100vw - ${band.lead + panel}px)))`;

export type Stacking = {
  current: string | null;
  me: string | null;
  focused: boolean;
  open: (card: Card) => void;
  focus: () => void;
  collapseFocus: () => void;
  leaveFocus: (path: string) => void;
  back: () => void;
  close: () => void;
};

const nobody: Stacking = {
  current: null,
  me: null,
  focused: false,
  open: () => undefined,
  focus: () => undefined,
  collapseFocus: () => undefined,
  leaveFocus: () => undefined,
  back: () => undefined,
  close: () => undefined,
};
const TheStack = createContext<Stacking>(nobody);

export function useStack(): Stacking {
  return useContext(TheStack);
}

export function StackProvider({ value, children }: { value: Stacking; children: React.ReactNode }) {
  return <TheStack.Provider value={value}>{children}</TheStack.Provider>;
}

export type PanelAction = {
  key: string;
  icon: IconName;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
};

type RegisteredActions = { for: string; actions: readonly PanelAction[] };
type ActionChannel = {
  card: string;
  put: (registration: RegisteredActions) => void;
  clear: (card: string) => void;
};
const ThePanelActions = createContext<ActionChannel | null>(null);

export function usePanelActions(actions: readonly PanelAction[]): void {
  const channel = useContext(ThePanelActions);
  const latest = useRef(actions);
  latest.current = actions;
  const shape = actions
    .map((action) => `${action.key}:${action.icon}:${action.label}:${action.disabled ? "off" : "on"}`)
    .join("|");
  const held = useRef<{ shape: string; actions: readonly PanelAction[] }>({ shape: "", actions: [] });
  if (held.current.shape !== shape) {
    held.current = {
      shape,
      actions: actions.map((action) => ({
        ...action,
        onPress: () => latest.current.find((candidate) => candidate.key === action.key)?.onPress(),
      })),
    };
  }
  const stable = held.current.actions;
  useEffect(() => {
    if (!channel) return;
    channel.put({ for: channel.card, actions: stable });
    return () => channel.clear(channel.card);
  }, [channel, stable]);
}

function useLeaving(card: Card | null, abrupt: boolean): { drawn: Card | null; leaving: boolean } {
  const theme = useTheme();
  const [ghost, setGhost] = useState<Card | null>(card);
  useEffect(() => {
    if (card || abrupt || theme.preferReduced || !onWeb) {
      setGhost(card);
      return;
    }
    const timer = setTimeout(() => setGhost(null), motion.slow.duration);
    return () => clearTimeout(timer);
  }, [card, abrupt, theme.preferReduced]);
  return { drawn: card ?? ghost, leaving: !card && ghost !== null };
}

export function Panel({
  card,
  under,
  wide,
  fixed = false,
  joined = false,
  glass = false,
  focused = false,
  testID,
}: {
  card: Card;

  under: boolean;
  wide: boolean;

  fixed?: boolean;

  joined?: boolean;

  glass?: boolean;
  focused?: boolean;
  testID?: string;
}) {
  const theme = useTheme();
  const stack = useStack();
  const [registered, setRegistered] = useState<RegisteredActions>({ for: "", actions: [] });
  const put = useCallback((next: RegisteredActions) => setRegistered(next), []);
  const clear = useCallback(
    (from: string) => setRegistered((held) => (held.for === from ? { for: "", actions: [] } : held)),
    [],
  );
  const channel = useMemo<ActionChannel>(() => ({ card: card.key, put, clear }), [card.key, put, clear]);
  const actions = registered.for === card.key ? registered.actions : [];
  const [focusRegistration, setFocusRegistration] = useState<FocusRegistration | null>(null);
  const focusPut = useCallback((next: FocusRegistration) => setFocusRegistration(next), []);
  const focusClear = useCallback(
    (from: string) => setFocusRegistration((held) => (held?.for === from ? null : held)),
    [],
  );
  const focusChannel = useMemo(
    () => ({ card: card.key, put: focusPut, clear: focusClear }),
    [card.key, focusPut, focusClear],
  );
  const chrome = focusRegistration?.for === card.key ? focusRegistration.chrome : null;
  return (
    <Col
      testID={testID ?? `panel-${card.key}`}
      style={{
        width: "100%",
        height: "100%",

        position: "relative",
        ...(fixed ? { zIndex: 4 } : {}),
        backgroundColor: theme.color["bg.surface"],
        borderWidth: wide && !focused && !joined ? 1 : 0,
        borderColor: theme.color["border.default"],

        borderRadius: wide && !focused && !joined ? theme.radius.xl : 0,
        ...(joined && fixed ? { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : {}),
        ...(joined && !fixed ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 } : {}),
        overflow: "hidden",
        ...(glass && !joined ? frosted(theme, 30) : {}),
        ...(wide && !joined ? theme.elevation.raised : {}),
      }}
    >
      {focused ? (
        <FocusBanner title={card.title} chrome={chrome} actions={actions} onBack={stack.collapseFocus} />
      ) : (
        <Col
          testID={`panel-head-${card.key}`}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: band.gap,
            height: CONTENT_HEADER_HEIGHT,

            paddingHorizontal: fixed ? theme.space[4] : theme.space[2],
            borderBottomWidth: 1,
            borderBottomColor: theme.color["border.default"],
          }}
        >
          {fixed ? null : (
            <IconButton
              icon={under ? "arrow-left" : "close"}
              ariaLabel={under ? say("panel.back") : say("panel.close")}
              testID={under ? "panel-back" : "panel-close"}
              onPress={() => (under ? stack.back() : stack.close())}
            />
          )}
          <Text
            typeName="bodySemibold"
            color="text.primary"
            numberOfLines={1}
            style={{ flex: 1, minWidth: 0 }}
          >
            {card.title}
          </Text>
          {actions.map((action) => (
            <IconButton
              key={action.key}
              icon={action.icon}
              ariaLabel={action.label}
              testID={action.testID}
              disabled={action.disabled}
              onPress={action.onPress}
            />
          ))}
          {fixed ? (
            <IconButton
              icon="close"
              ariaLabel={say("panel.close")}
              testID="details-close"
              onPress={() => showDetails(false)}
            />
          ) : null}
        </Col>
      )}
      <ScrollArea
        testID={`panel-scroll-${card.key}`}
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: theme.space[4],
          gap: theme.space[4],
          ...(focused ? { minHeight: "100%" } : {}),
        }}
      >
        <ThePanelActions.Provider value={channel}>
          <FocusChromeProvider value={focusChannel}>
            <Changed key={card.key}>{card.render()}</Changed>
          </FocusChromeProvider>
        </ThePanelActions.Provider>
      </ScrollArea>
    </Col>
  );
}

export function StandalonePanel({ card }: { card: Card }) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  if (narrow) {
    return (
      <Col
        testID="standalone-resource"
        style={{ position: "absolute", top: band.heightNarrow, right: 0, bottom: 0, left: 0, zIndex: 2 }}
      >
        <Panel card={card} under={false} wide={false} />
      </Col>
    );
  }
  return (
    <Col
      testID="standalone-resource"
      style={{ width: A_RESOURCE_PANEL_IS, paddingRight: theme.space[3], paddingBottom: theme.space[3] }}
    >
      <Panel card={card} under={false} wide />
    </Col>
  );
}

export function Panels({
  standing,
  open,
  abrupt = false,
}: {
  standing: Card | null;
  open: Card | null;

  abrupt?: boolean;
}) {
  const theme = useTheme();
  const narrow = theme.isAtMost(breakpoints.phone);
  const stack = useStack();
  const trail = useDragSize({
    axis: "x",
    initial: rememberedTrail(),
    min: A_PANEL_LEAST,
    max: A_PANEL_MOST,
    label: "resize the panel",
    grows: "before",
  });
  useEffect(() => {
    try {
      if (typeof localStorage !== "undefined")
        localStorage.setItem(TRAIL_KEY, String(Math.round(trail.size)));
    } catch {}
  }, [trail.size]);
  const panels = useRef<HTMLElement | null>(null);
  const resource = useLeaving(open, abrupt);
  const details = useLeaving(standing, abrupt);
  const dismiss = useCallback(() => {
    if (stack.focused) stack.collapseFocus();
    else if (open) stack.back();
    else if (narrow && standing) stack.close();
  }, [narrow, open, stack, standing]);

  useEffect(() => {
    if (!open && !(narrow && standing)) return;
    const pointer = (event: PointerEvent) => {
      if (event.button !== 0 || panels.current?.contains(event.target as Node)) return;
      dismiss();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };
    document.addEventListener("pointerdown", pointer, true);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", pointer, true);
      document.removeEventListener("keydown", key);
    };
  }, [dismiss, narrow, open, standing]);

  if (narrow) {
    if (!details.drawn && !resource.drawn) return null;
    return (
      <Col
        ref={panels as never}
        testID="panels"
        style={{
          position: "absolute",

          top: stack.focused ? 0 : band.heightNarrow,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: stack.focused ? 100 : 2,
        }}
      >
        {details.drawn ? (
          <TheDrawer
            card={details.drawn}
            leaving={details.leaving}
            under={false}
            onDismiss={() => (stack.focused ? stack.collapseFocus() : stack.close())}
          />
        ) : null}
        {resource.drawn ? (
          <TheDrawer
            card={resource.drawn}
            leaving={resource.leaving}
            under={Boolean(standing)}
            focused={stack.focused}
            onDismiss={() => (stack.focused ? stack.collapseFocus() : stack.back())}
          />
        ) : null}
      </Col>
    );
  }

  if (!standing && !resource.drawn) return null;

  return (
    <Col
      ref={panels as never}
      testID="panels"
      style={{
        width: trail.size,
        flexGrow: 0,
        flexShrink: 0,
        alignSelf: "stretch",

        paddingRight: theme.space[3],
        paddingBottom: theme.space[3],
      }}
    >
      {standing ? (
        <Panel card={standing} under={false} wide fixed joined testID={`panel-${standing.key}`} />
      ) : null}
      {resource.drawn ? (
        <Col
          testID="resource-extension"
          style={
            {
              position: stack.focused ? "fixed" : "absolute",
              ...(stack.focused
                ? { top: 0, right: 0, bottom: 0, left: 0, width: "100vw" }
                : {
                    top: 0,
                    bottom: theme.space[3],
                    right: trail.size,
                    width: resourcePanelWidth(trail.size),
                  }),
              zIndex: stack.focused ? 100 : 3,
              transform: `translateX(${resource.leaving ? "100%" : "0"})`,
              ...(theme.preferReduced
                ? {}
                : {
                    transitionProperty: "transform",
                    transitionDuration: `${motion.slow.duration}ms`,
                    transitionTimingFunction: motion.slow.easing,
                  }),
            } as object
          }
        >
          <Slid card={resource.drawn} />
        </Col>
      ) : null}
      <Col
        {...trail.props}
        testID="panel-resize"
        style={{
          ...(trail.props.style as ViewStyle),
          position: "absolute",
          top: 0,
          bottom: 0,
          left: -4,
          width: 8,
          zIndex: 5,
          backgroundColor: trail.dragging ? theme.color.accentSoft : "transparent",
        }}
      />
    </Col>
  );
}

function Changed({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const [arrived, setArrived] = useState(theme.preferReduced);
  useEffect(() => {
    if (theme.preferReduced) return;
    const frame = requestAnimationFrame(() => setArrived(true));
    return () => cancelAnimationFrame(frame);
  }, [theme.preferReduced]);
  return (
    <Col
      style={
        {
          opacity: arrived ? 1 : 0,
          transform: `translateY(${arrived ? 0 : 6}px)`,
          ...(theme.preferReduced
            ? {}
            : {
                transitionProperty: "opacity, transform",
                transitionDuration: "120ms",
                transitionTimingFunction: motion.fast.easing,
              }),
        } as object
      }
    >
      {children}
    </Col>
  );
}

function Slid({ card }: { card: Card }) {
  const theme = useTheme();
  const stack = useStack();
  const [arrived, setArrived] = useState(theme.preferReduced);
  useEffect(() => {
    setArrived(false);
    const frame = requestAnimationFrame(() => setArrived(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <Col
      style={
        {
          width: "100%",
          height: "100%",
          transform: `translateX(${arrived ? "0" : "100%"})`,
          ...(theme.preferReduced
            ? {}
            : {
                transitionProperty: "transform",
                transitionDuration: `${motion.slow.duration}ms`,
                transitionTimingFunction: motion.slow.easing,
              }),
        } as object
      }
    >
      <Panel card={card} under wide joined={!stack.focused} glass={!stack.focused} focused={stack.focused} />
    </Col>
  );
}
